import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { useResource } from "../../hooks/useResource.js";
import { DataTable } from "../../components/data-table/DataTable.jsx";
import { Pagination } from "../../components/data-table/Pagination.jsx";
import { FilterBar } from "../../components/data-table/FilterBar.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { Badge } from "../../components/ui/Badge.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { RowActions } from "../../components/ui/RowActions.jsx";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog.jsx";
import { ImageThumbnail } from "../../components/upload/ImageUpload.jsx";

const STATUS_TONE = { available: "success", reserved: "warning", sold: "default" };

export default function HouseLandPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    document.title = "House & Land · Admin";
  }, []);

  const { list, remove } = useResource("house-land", { toastLabel: "package" });
  const { data, isLoading } = list({
    page,
    limit: 12,
    sort: "sortOrder:asc",
    ...(search && { search }),
    ...(status && { status }),
  });

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent">Catalogue</p>
          <h1 className="mt-1 font-display text-2xl text-text-primary">House &amp; Land Packages</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {data?.meta?.total ?? "—"} package{data?.meta?.total === 1 ? "" : "s"} total
          </p>
        </div>
        <Link to="/house-land/new">
          <Button leftIcon={<Plus className="h-4 w-4" />}>New package</Button>
        </Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-border-subtle">
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by estate or suburb…"
          filters={
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="min-w-[150px]"
            >
              <option value="">Any status</option>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="sold">Sold</option>
            </Select>
          }
        />

        <DataTable
          loading={isLoading}
          rows={data?.items ?? []}
          onRowClick={(row) => navigate(`/house-land/${row._id}`)}
          columns={[
            {
              key: "estateName",
              label: "Package",
              render: (r) => (
                <div className="flex items-center gap-3">
                  <ImageThumbnail src={r.images?.[0]} alt={r.estateName} />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-text-primary">{r.estateName}</p>
                    <p className="truncate text-xs text-text-muted">
                      {r.suburb} · {r.direction?.replace(/_/g, " ")}
                    </p>
                  </div>
                </div>
              ),
            },
            {
              key: "linkedDesign",
              label: "Design",
              render: (r) => (
                <span className="text-text-secondary">{r.linkedDesign?.name ?? "—"}</span>
              ),
            },
            {
              key: "price",
              label: "Price",
              render: (r) =>
                r.price ? (
                  <span className="font-medium text-text-primary">
                    ${(r.price / 1000).toFixed(0)}k
                  </span>
                ) : (
                  "—"
                ),
            },
            {
              key: "status",
              label: "Status",
              render: (r) => <Badge tone={STATUS_TONE[r.status] ?? "default"}>{r.status}</Badge>,
            },
          ]}
          actions={(row) => (
            <RowActions
              onEdit={() => navigate(`/house-land/${row._id}`)}
              onDelete={() => setConfirmDelete(row)}
            />
          )}
          empty={{
            title: "No packages yet",
            description: "Create your first house & land package to populate the public listing.",
          }}
        />
        <Pagination meta={data?.meta} onChange={setPage} />
      </div>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title={`Delete "${confirmDelete?.estateName}"?`}
        description="The package will be removed from the public listing immediately."
        confirmLabel="Delete package"
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(confirmDelete._id, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
}
